import { useEffect, useRef } from 'react';

import { dispatchBack } from './backChain';
import { resolveTvKey, type TvKeyAction } from './keys';

export type KeyMapHandlers = Partial<Record<TvKeyAction, () => void>> & {
  /** Fires for every key press (before mapping) — used by the player HUD auto-hide. */
  onAnyKey?: (event: KeyboardEvent) => void;
};

export interface UseKeyMapOptions {
  enabled?: boolean;
  /**
   * When true (default) an unhandled `back` runs the global back chain
   * (`dispatchBack`). The app root uses this; nested consumers pass false.
   */
  dispatchBackChain?: boolean;
}

/**
 * Window-level listener for Back and media keys (docs/04 §2). Arrow/Enter keys are left to
 * norigin. Handlers are read through a ref so callers can pass inline closures.
 */
export function useKeyMap(
  handlers: KeyMapHandlers,
  { enabled = true, dispatchBackChain = false }: UseKeyMapOptions = {},
): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      ref.current.onAnyKey?.(event);
      const action = resolveTvKey(event);
      if (!action) return;
      if (event.repeat && action === 'back') return;
      event.preventDefault();
      const handler = ref.current[action];
      if (handler) {
        handler();
        return;
      }
      if (action === 'back' && dispatchBackChain) dispatchBack();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, dispatchBackChain]);
}
